/**
 * Default colour and display label for each site-layer category. UI consumers fall
 * back to this map when a SiteLayer has no `color` override. Hex values mirror
 * the AURORA theme palette (see {@link AURORA_OVERLAY_PALETTE}) so overlays read
 * consistently against the dark satellite map AND the report cover's dark scrim,
 * while staying legible when reused on the light report page (`'on-light'` tone).
 */
import type { SiteLayer, SiteLayerCategory } from '../types/site';

/**
 * AURORA OVERLAY PALETTE — the concrete hex values backing every map/SVG overlay
 * default in the app (category colours, per-parcel outlines, layer-editor swatch
 * presets). Maps + SVG need a CONCRETE colour, not a CSS `var()`, so these are
 * the resolved hexes of the Aurora design tokens (see `src/index.css`):
 *
 *   teal     — #2dd4bd  (Aurora accent, --cyan-400 ramp; the app's signature)
 *   cyan     — #22d3ee  (lighter cyan; pairs with the teal accent)
 *   violet   — #a78bfa  (--chart-3)
 *   amber    — #fbbf24  (--warning)
 *   rose     — #fb7185  (--chart-2)
 *   green    — #34d399  (--success)
 *   orange   — #fb923c  (--conditional-high, Aurora orange)
 *   slate    — #94a3b8  (neutral, for the catch-all "other")
 *
 * Every hue is high-saturation enough to read against the near-black map scrim
 * (#0d1117) yet has a darkened `'on-light'` sibling (see LAYER_COLORS_ON_LIGHT /
 * AURORA_OVERLAY_PALETTE_ON_LIGHT) for the pale report page. Distinct hues keep
 * the legend one-to-one with the drawn outlines.
 */
export const AURORA_OVERLAY_PALETTE = {
  teal:   '#2dd4bd',
  cyan:   '#22d3ee',
  violet: '#a78bfa',
  amber:  '#fbbf24',
  rose:   '#fb7185',
  green:  '#34d399',
  orange: '#fb923c',
  slate:  '#94a3b8',
} as const;

export const LAYER_COLORS: Record<SiteLayerCategory, string> = {
  'infrastructure.pipeline':    AURORA_OVERLAY_PALETTE.amber,  // #fbbf24 — pipelines
  'infrastructure.electrical':  AURORA_OVERLAY_PALETTE.teal,   // #2dd4bd — Aurora accent
  'infrastructure.fiber':       AURORA_OVERLAY_PALETTE.violet, // #a78bfa — fiber
  'infrastructure.transport':   AURORA_OVERLAY_PALETTE.green,  // #34d399 — transport / access
  'constraint.environmental':   AURORA_OVERLAY_PALETTE.rose,   // #fb7185 — constraints
  'concept.expansion':          AURORA_OVERLAY_PALETTE.orange, // #fb923c — expansion / planned
  'concept.generation':         AURORA_OVERLAY_PALETTE.teal,
  'concept.ccs':                AURORA_OVERLAY_PALETTE.violet,
  'impact.noise':               AURORA_OVERLAY_PALETTE.amber,
  'impact.receptor':            AURORA_OVERLAY_PALETTE.rose,
  'impact.barrier':             AURORA_OVERLAY_PALETTE.cyan,   // #22d3ee — acoustic screens
  'other':                      AURORA_OVERLAY_PALETTE.slate,  // #94a3b8 — catch-all
};

/**
 * Compact provenance badge per layer source, shown on layers-panel rows so
 * imported (KMZ/SHP/GeoJSON/DWG), fetched (ArcGIS/PeeringDB/LandGate), and
 * generated (scenario) geometry are distinguishable at a glance.
 */
export const SOURCE_BADGES: Record<SiteLayer['source'], string> = {
  'kmz-import': 'KMZ',
  'shapefile-import': 'SHP',
  'geojson-import': 'GeoJSON',
  'cad-import': 'CAD',
  manual: 'Manual',
  'arcgis-import': 'ArcGIS',
  'peeringdb-import': 'PeeringDB',
  'landgate-import': 'LandGate',
  'doc-derived': 'Doc',
  'scenario-derived': 'Generated',
};

export const CATEGORY_LABELS: Record<SiteLayerCategory, string> = {
  'infrastructure.pipeline':    'Pipelines',
  'infrastructure.electrical':  'Electrical',
  'infrastructure.fiber':       'Fiber',
  'infrastructure.transport':   'Transport / Access',
  'constraint.environmental':   'Constraints',
  'concept.expansion':          'Expansion / Planned',
  'concept.generation':         'Generation',
  'concept.ccs':                'CCS / Compression',
  'impact.noise':               'Noise screening',
  'impact.receptor':            'Impact receptors',
  'impact.barrier':             'Acoustic barriers',
  'other':                      'Other',
};

export const CATEGORY_ORDER: SiteLayerCategory[] = [
  'infrastructure.pipeline',
  'infrastructure.electrical',
  'infrastructure.fiber',
  'infrastructure.transport',
  'constraint.environmental',
  'concept.expansion',
  'concept.generation',
  'concept.ccs',
  'impact.noise',
  'impact.receptor',
  'impact.barrier',
  'other',
];

/**
 * Surface tone the category colour will sit ON. The default palette
 * ({@link LAYER_COLORS}) was tuned to read against the DARK satellite map
 * (`'on-dark'`). The professional report presets (journal / institutional /
 * monograph) frame the site-map figure on a LIGHT page, where some of those
 * bright dark-map hues (cyan / amber / violet) wash out. `'on-light'` returns
 * a slightly darkened, legibility-tuned variant of the SAME hue so a tag chip,
 * swatch, or SVG label stays readable on a pale figure background.
 */
export type LayerColorTone = 'on-dark' | 'on-light';

/**
 * On-light legibility variants of {@link LAYER_COLORS}. Same hue family as the
 * on-dark default, darkened toward WCAG-legible-on-paper. ONLY consulted when a
 * caller explicitly asks for the `'on-light'` tone; the default 1- and 2-arg
 * call signatures are untouched, so every existing caller (DOCX export, layer
 * editor, panel, sidebar) returns the exact same hex it always has.
 */
const LAYER_COLORS_ON_LIGHT: Record<SiteLayerCategory, string> = {
  'infrastructure.pipeline':    '#b45309', // amber-700  (on-dark Aurora amber  #fbbf24)
  'infrastructure.electrical':  '#0f766e', // teal-700   (on-dark Aurora teal   #2dd4bd)
  'infrastructure.fiber':       '#6d28d9', // violet-700 (on-dark Aurora violet #a78bfa)
  'infrastructure.transport':   '#047857', // emerald-700(on-dark Aurora green  #34d399)
  'constraint.environmental':   '#be123c', // rose-700   (on-dark Aurora rose   #fb7185)
  'concept.expansion':          '#c2410c', // orange-700 (on-dark Aurora orange #fb923c)
  'concept.generation':         '#0f766e',
  'concept.ccs':                '#6d28d9',
  'impact.noise':               '#b45309',
  'impact.receptor':            '#be123c',
  'impact.barrier':             '#0e7490', // cyan-700   (on-dark Aurora cyan   #22d3ee)
  'other':                      '#475569', // slate-600  (on-dark Aurora slate  #94a3b8)
};

/**
 * Resolve the display colour for a site-layer category.
 *
 * Precedence: an explicit per-layer `override` always wins (unchanged — this is
 * still the SINGLE colour chokepoint). Absent an override, the colour comes from
 * the tone-appropriate palette. `tone` defaults to `'on-dark'` so EVERY existing
 * caller (`colorForCategory(cat)` / `colorForCategory(cat, layer.color)`) returns
 * the identical hex it did before this param existed — pure back-compat.
 */
export function colorForCategory(
  category: SiteLayerCategory,
  override?: string,
  tone: LayerColorTone = 'on-dark',
): string {
  if (override) return override;
  return tone === 'on-light'
    ? LAYER_COLORS_ON_LIGHT[category]
    : LAYER_COLORS[category];
}

/**
 * AURORA PER-PARCEL OUTLINE PALETTE — the ordered hues handed to the per-parcel
 * map outlines + the matching legend dots (one-to-one). Indexed by parcel
 * position, wrapped modulo its length. Drawn from {@link AURORA_OVERLAY_PALETTE}
 * so the parcel outlines feel like the same Aurora theme as the category
 * overlays.
 *
 * Ordering deliberately leads with hues that DON'T collide with the two most
 * common in-SVG utility-line categories — pipelines (amber #fbbf24) and
 * electrical (teal #2dd4bd) — so a parcel outline can't be mistaken for a
 * pipeline/transmission line. The common 2-parcel case is cyan then violet
 * (both clearly distinct from the amber/teal utility lines); the amber + rose
 * tail only appears at ≥3 parcels (rare).
 *
 * `dot` feeds BOTH the overlay stroke (via `parcelStrokeColors`) and the legend
 * dot, so legend == map. `fill` is the same hue at low alpha for the map fill.
 */
export const AURORA_PARCEL_COLORS: Array<{ stroke: string; fill: string; dot: string }> = [
  { stroke: AURORA_OVERLAY_PALETTE.cyan,   fill: 'rgba(34, 211, 238, 0.18)', dot: AURORA_OVERLAY_PALETTE.cyan },   // #22d3ee — Site-I
  { stroke: AURORA_OVERLAY_PALETTE.violet, fill: 'rgba(167, 139, 250, 0.18)', dot: AURORA_OVERLAY_PALETTE.violet }, // #a78bfa — Site-II
  { stroke: AURORA_OVERLAY_PALETTE.rose,   fill: 'rgba(251, 113, 133, 0.18)', dot: AURORA_OVERLAY_PALETTE.rose },  // #fb7185
  { stroke: AURORA_OVERLAY_PALETTE.green,  fill: 'rgba(52, 211, 153, 0.18)', dot: AURORA_OVERLAY_PALETTE.green },  // #34d399
  { stroke: AURORA_OVERLAY_PALETTE.orange, fill: 'rgba(251, 146, 60, 0.18)', dot: AURORA_OVERLAY_PALETTE.orange }, // #fb923c
];
